import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  adminAuth,
  adminDb,
} from '../services/adminService';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(
    AdminAuthContext
  );

  if (!context) {
    throw new Error(
      'useAdminAuth must be used within an AdminAuthProvider'
    );
  }

  return context;
};

const normalizeCode = (value) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, 6);

export const AdminAuthProvider = ({
  children,
}) => {
  const [phase, setPhase] =
    useState('loading');
  const [session, setSession] =
    useState(null);
  const [adminContext, setAdminContext] =
    useState(null);
  const [factorId, setFactorId] =
    useState(null);
  const [enrollment, setEnrollment] =
    useState(null);
  const [error, setError] =
    useState('');
  const evaluationRef = useRef(0);
  const mountedRef = useRef(true);

  const evaluateSession = useCallback(
    async (nextSession) => {
      const evaluation =
        ++evaluationRef.current;

      setError('');
      setSession(nextSession || null);
      setAdminContext(null);
      setEnrollment(null);

      if (!nextSession?.user) {
        setFactorId(null);
        setPhase('signed_out');
        return;
      }

      setPhase('loading');

      try {
        const assurance =
          await adminAuth.getAssuranceLevel();

        if (assurance.error) {
          throw assurance.error;
        }

        if (
          assurance.data.currentLevel ===
          'aal2'
        ) {
          const context =
            await adminDb.getContext();

          if (
            evaluation !==
              evaluationRef.current ||
            !mountedRef.current
          ) {
            return;
          }

          setAdminContext(context);
          setFactorId(null);
          setPhase('authorized');
          return;
        }

        const factors =
          await adminAuth.listFactors();

        if (factors.error) {
          throw factors.error;
        }

        const verifiedFactor =
          factors.data.totp.find(
            (factor) =>
              factor.status === 'verified'
          );

        if (
          evaluation !==
            evaluationRef.current ||
          !mountedRef.current
        ) {
          return;
        }

        if (verifiedFactor) {
          setFactorId(verifiedFactor.id);
          setPhase('challenge');
        } else {
          setFactorId(null);
          setPhase('enrollment_required');
        }
      } catch (nextError) {
        if (
          evaluation !==
            evaluationRef.current ||
          !mountedRef.current
        ) {
          return;
        }

        const message = String(
          nextError?.message || ''
        );

        if (
          message.includes(
            'Admin access denied'
          )
        ) {
          setPhase('denied');
          return;
        }

        setError(
          'We could not verify administrator access. Please try again.'
        );
        setPhase('error');
      }
    },
    []
  );

  useEffect(() => {
    mountedRef.current = true;

    const { data: { subscription } } =
      adminAuth.onAuthStateChange(
        (_event, nextSession) => {
          window.setTimeout(() => {
            if (mountedRef.current) {
              evaluateSession(nextSession);
            }
          }, 0);
        }
      );

    adminAuth.getSession()
      .then(({ data, error: sessionError }) => {
        if (sessionError) {
          throw sessionError;
        }

        return evaluateSession(
          data.session
        );
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setError(
          'Administrator authentication could not be initialized.'
        );
        setPhase('error');
      });

    return () => {
      mountedRef.current = false;
      subscription?.unsubscribe?.();
    };
  }, [evaluateSession]);

  const signIn = async (
    email,
    password
  ) => {
    setError('');
    setPhase('loading');

    const { data, error: signInError } =
      await adminAuth.signIn(
        email.trim(),
        password
      );

    if (signInError) {
      setPhase('signed_out');
      setError(
        'The email or password is incorrect.'
      );
      return false;
    }

    await evaluateSession(data.session);
    return true;
  };

  const signOut = async () => {
    evaluationRef.current += 1;
    await adminAuth.signOut();
    setSession(null);
    setAdminContext(null);
    setFactorId(null);
    setEnrollment(null);
    setError('');
    setPhase('signed_out');
  };

  const beginEnrollment = async () => {
    setError('');
    setPhase('loading');

    try {
      const factors =
        await adminAuth.listFactors();

      if (factors.error) throw factors.error;

      const unverifiedFactors =
        factors.data.totp.filter(
          (factor) =>
            factor.status !== 'verified'
        );

      for (const factor of unverifiedFactors) {
        const removal =
          await adminAuth.unenrollFactor(
            factor.id
          );

        if (removal.error) {
          throw removal.error;
        }
      }

      const result =
        await adminAuth.enrollTotp();

      if (result.error) throw result.error;

      setFactorId(result.data.id);
      setEnrollment(result.data.totp);
      setPhase('enrollment');
    } catch (nextError) {
      setError(
        nextError?.message ||
          'MFA enrollment could not be started.'
      );
      setPhase('enrollment_required');
    }
  };

  const verifyCode = async (
    rawCode,
    isEnrollment = false
  ) => {
    const code = normalizeCode(rawCode);

    if (code.length !== 6 || !factorId) {
      setError(
        'Enter the six-digit code from your authenticator app.'
      );
      return false;
    }

    setError('');

    const returnPhase = isEnrollment
      ? 'enrollment'
      : 'challenge';

    setPhase('loading');

    try {
      const challenge =
        await adminAuth.challengeFactor(
          factorId
        );

      if (challenge.error) {
        throw challenge.error;
      }

      const verification =
        await adminAuth.verifyFactor({
          factorId,
          challengeId: challenge.data.id,
          code,
        });

      if (verification.error) {
        throw verification.error;
      }

      const refreshed =
        await adminAuth.refreshSession();

      if (refreshed.error) {
        throw refreshed.error;
      }

      await evaluateSession(
        refreshed.data.session
      );
      return true;
    } catch (nextError) {
      setError(
        'That code could not be verified. Wait for a new code and try again.'
      );
      setPhase(returnPhase);
      return false;
    }
  };

  return (
    <AdminAuthContext.Provider
      value={{
        phase,
        session,
        adminContext,
        enrollment,
        error,
        setError,
        signIn,
        signOut,
        beginEnrollment,
        verifyEnrollment: (code) =>
          verifyCode(code, true),
        verifyChallenge: (code) =>
          verifyCode(code, false),
        retry: () =>
          evaluateSession(session),
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};
